import Styled, {keyframes} from 'styled-components';
import {
	NOVA_PURPLE,
	NOVA_PURPLE_DARK,
	NOVA_AVATAR_GRADIENT,
	TEXT_MAIN,
	TEXT_AUX,
	TEXT_BODY,
	TEXT_BANNER,
	TEXT_MED,
	TEXT_FIELD_LABEL,
	SUCCESS_COLOR,
	SUCCESS_BRIGHT,
	ERROR_COLOR_3,
	ERROR_BG,
	BG_WHITE,
	BG_LIGHT,
	BG_PANEL,
	BG_DISABLED,
	BG_DOT_PENDING,
	BG_DROPZONE,
	BG_DELIVERABLE_ROW,
	BORDER_DELIVERABLE_ROW,
	BG_PLACEHOLDER_ROW,
	BORDER_PLACEHOLDER_ROW,
	BG_CLIENT_DROPDOWN_HOVER,
	BG_PILL_HOVER,
	BORDER_INPUT,
	BORDER_DIVIDER,
	BORDER_DOT_PENDING,
	BORDER_HOVER,
	BORDER_DROPZONE,
	BORDER_DROPZONE_ICON,
	ICON_BOX_BG,
	DOT_AMBER,
} from './colors';

export const FONT = "'neue-haas-grotesk-text', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

export const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

// ── Layout ────────────────────────────────────────────────────────────────────

export const Body = Styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: hidden;
`;

export const LeftPanel = Styled.div`
  width: 40%;
  min-width: 40%;
  display: flex;
  flex-direction: column;
  border-right: 1.5px solid #f0f0f5;
  overflow: hidden;
  background: linear-gradient(180deg, #fafbfd 0%, #fff 100%);
`;

export const RightPanel = Styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
`;

export const PanelLabel = Styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #9b7edb;
  padding: 12px 16px 8px;
  flex-shrink: 0;
  background: linear-gradient(90deg, rgba(110, 15, 234, 0.02) 0%, transparent 100%);
  border-bottom: 1px solid rgba(110, 15, 234, 0.05);
`;

export const Divider = Styled.div`
  width: 1.5px;
  background: linear-gradient(180deg, #f0f0f5 0%, #e8e8f0 50%, #f0f0f5 100%);
  flex-shrink: 0;
`;

// ── Chat panel ────────────────────────────────────────────────────────────────

export const MessagesArea = Styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const UserBubble = Styled.div`
  align-self: flex-end;
  background: #6e0fea;
  color: #fff;
  border-radius: 12px 12px 2px 12px;
  max-width: 80%;
  padding: 8px 12px;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const AssistantBubble = Styled.div<{compact?: boolean}>`
  align-self: ${({compact}) => (compact ? 'stretch' : 'flex-start')};
  max-width: ${({compact}) => (compact ? 'none' : '80%')};
  background: #fff;
  border: 1px solid #e8e8f0;
  border-radius: 2px 12px 12px 12px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.55;
  color: #2e2541;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const QuickReplyRow = Styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
  align-self: flex-start;
`;

export const QuickReplyBtn = Styled.button`
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #d8d8e8;
  background: #fff;
  color: #2e2541;
  transition: border-color 0.15s, background 0.15s;
  &:hover {
    border-color: #6e0fea;
    background: rgba(110,15,234,0.04);
    color: #6e0fea;
  }
`;

export const Cursor = Styled.span`
  display: inline-block;
  width: 2px;
  height: 13px;
  background: #2e2541;
  margin-left: 1px;
  vertical-align: middle;
  animation: ${blink} 0.8s step-end infinite;
`;

export const ToolChipsRow = Styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 2px;
`;

export const ChipCount = Styled.span`
  opacity: 0.6;
`;

export const ToolChip = Styled.div<{done: boolean}>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  background: ${({done}) => (done ? 'rgba(34, 197, 94, 0.08)' : 'rgba(110, 15, 234, 0.07)')};
  border: 1px solid ${({done}) => (done ? 'rgba(34, 197, 94, 0.3)' : 'rgba(110, 15, 234, 0.2)')};
  color: ${({done}) => (done ? '#16a34a' : '#6e0fea')};
  transition: background 0.3s, border-color 0.3s, color 0.3s;
`;

export const ChipDot = Styled.span<{done: boolean}>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({done}) => (done ? '#22c55e' : '#6e0fea')};
  flex-shrink: 0;
  animation: ${({done}) => (done ? 'none' : 'pulse 1.4s ease-in-out infinite')};
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(0.7); }
  }
`;

export const InputArea = Styled.div<{focused: boolean; compact?: boolean}>`
  padding: ${({compact}) => (compact ? '6px 12px' : '10px 12px')};
  border-top: 1px solid #eee;
  flex-shrink: 0;
`;

export const InputBox = Styled.div<{focused: boolean; disabled: boolean}>`
  border: 1.5px solid ${({focused}) => (focused ? '#6e0fea' : '#e0e0e8')};
  border-radius: 10px;
  background: ${({disabled}) => (disabled ? '#fafafa' : '#fff')};
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s;
  position: relative;
`;

export const ChatTextarea = Styled.textarea<{compact?: boolean}>`
  flex: 1;
  border: none;
  outline: none;
  padding: 10px 12px 4px;
  font-size: 13px;
  font-family: ${FONT};
  resize: none;
  height: ${({compact}) => (compact ? '36px' : '52px')};
  color: #2e2541;
  background: transparent;
  &::placeholder { color: #c0bdd0; }
`;

export const InputFooter = Styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 4px 8px 6px;
  gap: 6px;
`;

export const SendButton = Styled.button<{disabled?: boolean}>`
  width: 32px;
  height: 32px;
  background: ${({disabled}) => (disabled ? '#e8e4f8' : '#6e0fea')};
  color: ${({disabled}) => (disabled ? '#b39ddb' : '#fff')};
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.15s;
  &:hover:not([disabled]) { background: #560eb3; }
`;

export const StopButton = Styled.button`
  height: 28px;
  background: #fff0f0;
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 0 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.15s;
  &:hover { background: #fee2e2; }
`;

export const AttachmentRow = Styled.div`
  padding: 0 12px 4px;
`;

export const AttachmentChip = Styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(110, 15, 234, 0.06);
  border: 1px solid rgba(110, 15, 234, 0.2);
  border-radius: 6px;
  padding: 4px 8px 4px 10px;
  font-size: 11px;
  color: #6e0fea;
  max-width: 100%;
  overflow: hidden;
`;

export const ChipFilename = Styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

export const ChipRemove = Styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #9c65e8;
  font-size: 13px;
  line-height: 1;
  flex-shrink: 0;
  &:hover { color: #6e0fea; }
`;

export const AttachBtn = Styled.button<{disabled?: boolean}>`
  background: none;
  border: none;
  padding: 0 4px;
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${({disabled}) => (disabled ? '#d0c8e8' : '#a090c8')};
  font-size: 16px;
  display: flex;
  align-items: center;
  line-height: 1;
  &:hover:not([disabled]) { color: #6e0fea; }
`;

export const AttachDropdown = Styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  background: #fff;
  border: 1px solid #e0e0e8;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.10);
  min-width: 180px;
  z-index: 10;
  overflow: hidden;
`;

export const AttachOption = Styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: none;
  border: none;
  padding: 10px 14px;
  font-size: 13px;
  font-family: ${FONT};
  color: #2e2541;
  cursor: pointer;
  text-align: left;
  &:hover { background: rgba(110, 15, 234, 0.05); }
  & + & { border-top: 1px solid #f0f0f4; }
`;

export const AttachError = Styled.div`
  padding: 0 12px 4px;
  font-size: 11px;
  color: #ef4444;
`;

export const TypingBubble = Styled.div`
  align-self: flex-start;
  background: #f4f4f8;
  border-radius: 2px 10px 10px 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const TypingDot = Styled.span<{delay: string}>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9b8ec4;
  display: inline-block;
  animation: ${bounce} 1.2s ease-in-out infinite;
  animation-delay: ${({delay}) => delay};
`;

export const WelcomeArea = Styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 14px 8px;
`;

export const WelcomeTitle = Styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2e2541;
  margin-bottom: 4px;
`;

export const WelcomeSubtitle = Styled.div`
  font-size: 12px;
  color: #a1a1a1;
  margin-bottom: 16px;
  line-height: 1.5;
`;

export const SuggestionLabel = Styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #c4b5e8;
  margin-bottom: 8px;
`;

export const SuggestionCard = Styled.button`
  width: 100%;
  text-align: left;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 12px;
  font-family: ${FONT};
  color: #535353;
  cursor: pointer;
  margin-bottom: 6px;
  line-height: 1.4;
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: rgba(110, 15, 234, 0.04);
    border-color: rgba(110, 15, 234, 0.25);
    color: #2e2541;
  }
`;

// ── Proposal panel ────────────────────────────────────────────────────────────

export const ProposalScroll = Styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px 16px 16px;
`;

export const Placeholder = Styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a1a1a1;
  font-size: 13px;
  padding: 20px;
  text-align: center;
`;

export const PlaceholderContent = Styled.div`
  text-align: center;
`;

export const PlaceholderEmoji = Styled.div`
  font-size: 32px;
  margin-bottom: 10px;
`;

export const PlaceholderTitle = Styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #2e2541;
  margin-bottom: 6px;
`;

export const PlaceholderDesc = Styled.div`
  font-size: 12px;
  line-height: 1.5;
`;

export const EmptyText = Styled.div`
  color: #a1a1a1;
  font-size: 12px;
`;

export const FieldGroup = Styled.div`
  margin-bottom: 14px;
`;

export const FieldLabel = Styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #a1a1a1;
  margin-bottom: 4px;
`;

export const FieldInput = Styled.input`
  width: 100%;
  border: 1px solid #e0e0e8;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: ${FONT};
  color: #2e2541;
  box-sizing: border-box;
  outline: none;
  background: #fff;
  &:focus { border-color: #6e0fea; box-shadow: 0 0 0 2px rgba(110,15,234,0.08); }
  &:read-only { background: #fafafa; color: #a1a1a1; cursor: default; }
`;

// Same visual treatment as FieldInput, but for a <select> — a distinct styled component
// rather than FieldInput with an `as="select"` override, since a select can't take the same
// value/children typing as an input.
export const FieldSelect = Styled.select`
  width: 100%;
  border: 1px solid #e0e0e8;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: ${FONT};
  color: #2e2541;
  box-sizing: border-box;
  outline: none;
  background: #fff;
  &:focus { border-color: #6e0fea; box-shadow: 0 0 0 2px rgba(110,15,234,0.08); }
`;

export const DateRow = Styled.div`
  display: flex;
  gap: 12px;
`;

export const DateFieldGroup = Styled.div`
  flex: 1;
`;

export const ColorRow = Styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const ColorCircle = Styled.button<{color: string; selected: boolean}>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({color}) => color};
  border: ${({selected}) => (selected ? '2px solid #2e2541' : '2px solid transparent')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #fff;
  padding: 0;
  flex-shrink: 0;
  transition: transform 0.1s;
  &:hover { transform: scale(1.15); }
`;

export const BudgetPillRow = Styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const BudgetPill = Styled.button<{selected: boolean}>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-family: ${FONT};
  cursor: pointer;
  border: none;
  background: ${({selected}) => (selected ? NOVA_PURPLE : BG_DISABLED)};
  color: ${({selected}) => (selected ? BG_WHITE : TEXT_BODY)};
  transition: background 0.15s, color 0.15s;
  &:hover:not(:disabled) {
    background: ${({selected}) => (selected ? NOVA_PURPLE_DARK : BG_PILL_HOVER)};
  }
`;

export const TeamList = Styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TeamMemberRow = Styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const Avatar = Styled.div<{bg: string}>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({bg}) => bg};
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const TeamMemberInfo = Styled.div`
  flex: 1;
  min-width: 0;
`;

export const TeamMemberName = Styled.div`
  font-size: 13px;
  color: #2e2541;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TeamMemberRole = Styled.div`
  font-size: 11px;
  color: #a1a1a1;
`;

export const HoursBadge = Styled.span`
  background: #f3f3f3;
  font-size: 10px;
  border-radius: 3px;
  padding: 2px 6px;
  color: #535353;
  white-space: nowrap;
`;

export const OverallocatedIcon = Styled.span`
  color: #f3a030;
  font-size: 13px;
`;

export const WarningsBanner = Styled.div`
  background: #fff8e6;
  border: 1px solid #f3a030;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 12px;
  color: #535353;
  margin-bottom: 12px;
`;

export const WarningItem = Styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  & + & { margin-top: 4px; }
`;

// --- Drop zone entry ---
export const EntryBody = Styled.div`
    display: flex;
    align-items: stretch;
    gap: 16px;
    padding: 24px;
`;

export const DropZoneCard = Styled.div<{isDragging?: boolean; isDisabled?: boolean}>`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 32px 24px;
    border-radius: 12px;
    border: 2px dashed ${({isDragging}) => (isDragging ? NOVA_PURPLE : BORDER_DROPZONE)};
    background: ${({isDragging}) =>
		isDragging
			? 'rgba(110,15,234,0.06)'
			: `repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 8px,
                rgba(173, 205, 245, 0.35) 8px,
                rgba(173, 205, 245, 0.35) 9px
              ), ${BG_DROPZONE}`};
    cursor: ${({isDisabled}) => (isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({isDisabled}) => (isDisabled ? 0.5 : 1)};
    transition: border-color 0.15s;
    text-align: center;
    /* The hover affordance has to go with the click handler, or a dead zone still lights up. */
    &:hover { border-color: ${({isDisabled}) => (isDisabled ? BORDER_DROPZONE : NOVA_PURPLE)}; }
`;

export const DropZoneIconBox = Styled.div`
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: ${BG_WHITE};
    border: 1.5px solid ${BORDER_DROPZONE_ICON};
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;

export const DropZoneIconDot = Styled.div`
    position: absolute;
    top: -3px;
    right: -3px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${DOT_AMBER};
    border: 2px solid ${BG_WHITE};
`;

export const DropZoneTitle = Styled.div`
    font-size: 14px; font-weight: 600; color: ${TEXT_MAIN};
`;
export const DropZoneBrowse = Styled.span`
    text-decoration: underline; color: ${NOVA_PURPLE}; cursor: pointer; font-weight: 600;
`;
export const DropZoneSub = Styled.div`
    font-size: 13px; color: ${TEXT_MED}; line-height: 1.5;
`;
export const FormatBadges = Styled.div`
    display: flex; gap: 6px; flex-wrap: wrap; justify-content: center;
`;
export const FormatBadge = Styled.span`
    font-size: 11px; font-weight: 600; color: ${NOVA_PURPLE};
    background: rgba(110,15,234,0.08); border-radius: 4px; padding: 2px 7px;
`;

export const ManualCard = Styled.button`
    flex: 1;
    display: flex; flex-direction: column; align-items: flex-start; gap: 10px;
    padding: 24px; border-radius: 12px; border: 1.5px solid ${BORDER_INPUT};
    background: ${BG_LIGHT}; cursor: pointer; text-align: left;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    &:hover { border-color: ${BORDER_HOVER}; box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
`;
export const ManualCardTitle = Styled.div`
    font-size: 14px; font-weight: 700; color: ${TEXT_MAIN};
`;
export const ManualCardDesc = Styled.div`
    font-size: 12px; color: ${TEXT_MED}; line-height: 1.5;
`;
export const ManualCardTime = Styled.div`
    display: flex; align-items: center; gap: 4px;
    font-size: 11px; color: ${TEXT_AUX}; margin-top: 4px;
`;
export const EntryPrivacyFooter = Styled.div`
    padding: 12px 24px; font-size: 12px; color: ${TEXT_AUX};
    border-top: 1px solid ${BORDER_DIVIDER};
`;

// --- Extraction progress ---
export const ExtractionBody = Styled.div`
    padding: 24px; display: flex; flex-direction: column; gap: 16px;
`;

export const ExtractionCard = Styled.div`
    background: ${BG_WHITE};
    border: 1px solid ${BORDER_INPUT};
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const FileCard = Styled.div`
    display: flex; align-items: center; gap: 12px;
    padding: 10px 12px; border: 1px solid ${BORDER_INPUT}; border-radius: 8px; background: ${BG_PANEL};
`;
export const FileCardIcon = Styled.div`
    width: 36px; height: 36px; border-radius: 6px; background: ${ICON_BOX_BG};
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;
export const FileCardMeta = Styled.div`flex: 1; min-width: 0;`;
export const FileCardName = Styled.div`
    font-size: 13px; font-weight: 600; color: ${TEXT_MAIN};
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
export const FileCardSize = Styled.div`font-size: 11px; color: ${TEXT_AUX}; margin-top: 2px;`;
export const FileCardReplace = Styled.button`
    font-size: 13px; font-weight: 600; color: ${NOVA_PURPLE};
    background: none; border: none; cursor: pointer; padding: 4px 8px; border-radius: 6px;
    &:hover { background: rgba(110,15,234,0.06); }
`;
export const ExtractionStatus = Styled.div`
    display: flex; align-items: center; gap: 6px;
    font-size: 13px; color: ${TEXT_MAIN}; font-weight: 400;
    padding-bottom: 4px;
    border-bottom: 1px solid ${BORDER_DIVIDER};
`;
export const SpinnerIcon = Styled.span`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    animation: spin 1s linear infinite;
    @keyframes spin { to { transform: rotate(360deg); } }
`;
export const ChecklistContainer = Styled.div`display: flex; flex-direction: column; gap: 10px;`;
export const ChecklistRow = Styled.div`display: flex; align-items: center; gap: 10px; font-size: 13px;`;
export const ChecklistDot = Styled.div<{status: 'pending' | 'active' | 'done'}>`
    width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 10px;
    ${({status}) =>
		status === 'done'
			? `background: ${SUCCESS_BRIGHT}; color: ${BG_WHITE};`
			: status === 'active'
			? `border: 2px solid ${NOVA_PURPLE}; background: rgba(110,15,234,0.08);`
			: `border: 1.5px solid ${BORDER_DOT_PENDING}; background: ${BG_DOT_PENDING};`}
`;
export const ChecklistLabel = Styled.div<{status: 'pending' | 'active' | 'done'}>`
    color: ${({status}) => (status === 'pending' ? TEXT_AUX : TEXT_MAIN)};
    font-weight: ${({status}) => (status === 'active' ? '600' : '400')};
`;

export const RejectionContainer = Styled.div`
    display: flex; flex-direction: column; align-items: center; text-align: center;
    gap: 8px; padding: 24px 8px;
`;
export const RejectionIconWrap = Styled.div`
    width: 40px; height: 40px; border-radius: 50%; background: ${ERROR_BG};
    display: flex; align-items: center; justify-content: center; margin-bottom: 4px;
`;
export const RejectionTitle = Styled.div`font-size: 14px; font-weight: 600; color: ${TEXT_MAIN};`;
export const RejectionMessage = Styled.div`font-size: 13px; color: ${TEXT_MED}; max-width: 360px;`;

// SOW review form uses sentence-case labels (Figma design), not the uppercase FieldLabel
export const SowFieldLabel = Styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${TEXT_FIELD_LABEL};
    margin-bottom: 4px;
`;

// --- Review form panel ---
export const ReviewFormPanel = Styled.div`
    flex: 1; display: flex; flex-direction: column; overflow: hidden;
`;
export const ReviewFormScroll = Styled.div`flex: 1; overflow-y: auto; padding: 0 24px 24px;`;
export const SowBanner = Styled.div`
    margin: 16px 0 20px; padding: 10px 14px;
    background: rgba(110,15,234,0.04); border: 1px solid rgba(110,15,234,0.12);
    border-radius: 8px; font-size: 12.5px; color: ${TEXT_BANNER};
    display: flex; align-items: flex-start; gap: 8px; line-height: 1.5;
`;
export const SowBannerViewSource = Styled.a`
    font-size: 12px; font-weight: 600; color: ${NOVA_PURPLE}; text-decoration: none;
    white-space: nowrap; margin-left: auto; align-self: center; padding-left: 8px; cursor: pointer;
    &:hover { text-decoration: underline; }
`;
export const FormSectionHeader = Styled.div`
    font-size: 10px; font-weight: 700; letter-spacing: 0.8px; color: ${TEXT_AUX};
    text-transform: uppercase; padding: 16px 0 8px;
    border-bottom: 1px solid ${BORDER_DIVIDER}; margin-bottom: 12px;
`;
export const NovaFieldWrapper = Styled.div`position: relative;`;
export const NovaIndicatorBadge = Styled.div`
    position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
    font-size: 11px; color: ${NOVA_PURPLE}; pointer-events: none; opacity: 0.7;
`;
export const ReviewFooter = Styled.div`
    padding: 10px 24px; font-size: 12px; color: ${TEXT_MED};
    border-top: 1px solid ${BORDER_DIVIDER}; display: flex; align-items: center; gap: 4px;
`;
export const DeliverableRow = Styled.div`
    display: flex; align-items: center; gap: 10px;
    padding: 8px 14px; border-radius: 8px;
    background: ${BG_DELIVERABLE_ROW}; border: 1px solid ${BORDER_DELIVERABLE_ROW}; margin-bottom: 6px;
`;
export const DeliverableColorDot = Styled.div<{color?: string}>`
    width: 10px; height: 10px; border-radius: 50%;
    background: ${({color}) => color || TEXT_AUX}; flex-shrink: 0;
`;
export const DeliverableName = Styled.div`flex: 1; font-size: 13px; font-weight: 500; color: ${TEXT_MAIN};`;
export const DeliverableMeta = Styled.div`font-size: 12px; color: ${TEXT_AUX}; white-space: nowrap;`;

export const PlaceholderRow = Styled.div`
    display: flex; align-items: center; gap: 10px;
    padding: 7px 14px; border-radius: 8px;
    background: ${BG_PLACEHOLDER_ROW}; border: 1px solid ${BORDER_PLACEHOLDER_ROW}; margin-bottom: 6px;
`;
export const PlaceholderRoleName = Styled.div`flex: 1; font-size: 13px; font-weight: 500; color: ${TEXT_MAIN};`;
export const PlaceholderStatusLabel = Styled.div<{status: 'pending' | 'creating' | 'done' | 'failed'}>`
    font-size: 11px; font-weight: 600; white-space: nowrap;
    color: ${({status}) =>
		status === 'done'
			? SUCCESS_COLOR
			: status === 'failed'
			? ERROR_COLOR_3
			: status === 'creating'
			? NOVA_PURPLE
			: TEXT_AUX};
`;

// --- Right chat panel (review screen) ---
export const RightChatPanel = Styled.div`
    flex: 0 0 340px; display: flex; flex-direction: column;
    overflow: hidden; background: ${BG_WHITE};
`;
export const ChatPanelHeader = Styled.div`
    display: flex; align-items: center; gap: 10px;
    padding: 14px 16px 10px;
    border-bottom: 1px solid ${BORDER_DIVIDER};
`;
export const NovaAvatar = Styled.div`
    width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
    background: ${NOVA_AVATAR_GRADIENT};
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; color: ${BG_WHITE};
`;
export const ChatPanelHeaderText = Styled.div`
    display: flex; flex-direction: column; gap: 1px;
`;
export const ChatPanelTitle = Styled.div`
    font-size: 13px; font-weight: 700; color: ${TEXT_MAIN};
`;
export const ChatPanelSubtitle = Styled.div`
    font-size: 11px; color: ${TEXT_AUX};
`;

export const ClientDropdownChevron = Styled.div`
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: ${TEXT_AUX};
    font-size: 11px;
    line-height: 1;
`;

export const ClientDropdownList = Styled.ul`
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    right: 0;
    z-index: 100;
    background: ${BG_WHITE};
    border: 1px solid ${BORDER_INPUT};
    border-radius: 6px;
    max-height: 180px;
    overflow-y: auto;
    list-style: none;
    margin: 0;
    padding: 4px 0;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
`;

export const ClientDropdownItem = Styled.li`
    padding: 7px 12px;
    font-size: 13px;
    font-family: ${FONT};
    color: ${TEXT_MAIN};
    cursor: pointer;
    &:hover { background: ${BG_CLIENT_DROPDOWN_HOVER}; }
`;

// Title badge "· From SoW"
export const SowTitleBadge = Styled.span`
    display: inline-flex; align-items: center; gap: 3px;
    font-size: 11px; font-weight: 600; color: ${NOVA_PURPLE};
    background: rgba(110,15,234,0.08); border-radius: 20px;
    padding: 2px 8px; margin-left: 8px; vertical-align: middle;
    &::before {
        content: '✦';
        font-size: 14px;
        line-height: 1;
        color: ${NOVA_PURPLE};
    }
`;
